/**
 * Thin fetch wrapper.
 *
 * Auth is a session cookie, so every request sends credentials and every unsafe method
 * carries the CSRF token the server handed us at login.
 */

export class ApiError extends Error {
  constructor(status, body) {
    super(body?.message || `Request failed (${status})`);
    this.name = 'ApiError';
    this.status = status;
    this.code = body?.code || 'error';
    this.details = body?.details;
    this.body = body;
  }

  get isConflict() {
    return this.status === 409;
  }
  get isValidation() {
    return this.status === 422;
  }
  get isAuth() {
    return this.status === 401;
  }
}

let csrfToken = null;
let onUnauthorized = null;

export function setCsrfToken(token) {
  csrfToken = token || null;
}
export function getCsrfToken() {
  return csrfToken;
}
export function setUnauthorizedHandler(fn) {
  onUnauthorized = fn;
}

const UNSAFE = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

async function request(method, path, { body, signal, keepalive } = {}) {
  const headers = {};
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (UNSAFE.has(method) && csrfToken) headers['X-CSRF-Token'] = csrfToken;

  const response = await fetch(path, {
    method,
    headers,
    credentials: 'include',
    body: body === undefined ? undefined : JSON.stringify(body),
    signal,
    keepalive,
  });

  // The server rotates the CSRF token when it rotates the session.
  const rotated = response.headers.get('X-CSRF-Token');
  if (rotated) csrfToken = rotated;

  if (response.status === 204) return null;

  let payload = null;
  const type = response.headers.get('content-type') || '';
  if (type.includes('application/json')) {
    payload = await response.json().catch(() => null);
  }

  if (!response.ok) {
    if (response.status === 401 && onUnauthorized) onUnauthorized();
    throw new ApiError(response.status, payload);
  }
  return payload;
}

export const api = {
  get: (path, opts) => request('GET', path, opts),
  post: (path, body, opts) => request('POST', path, { ...opts, body }),
  put: (path, body, opts) => request('PUT', path, { ...opts, body }),
  patch: (path, body, opts) => request('PATCH', path, { ...opts, body }),
  del: (path, opts) => request('DELETE', path, opts),
};

/* --- endpoints ------------------------------------------------------------------ */

export const auth = {
  login: (username, password) => api.post('/api/auth/login', { username, password }),
  logout: () => api.post('/api/auth/logout'),
  me: () => api.get('/api/auth/me'),
  changePassword: (current_password, new_password) =>
    api.post('/api/auth/password', { current_password, new_password }),
};

export const meta = {
  bootstrap: () => api.get('/api/meta/bootstrap'),
  guidelines: () => api.get('/api/guidelines/current'),
  ackGuidelines: (version) => api.post('/api/guidelines/ack', { version }),
  // Read-only worked examples. There is deliberately no writing counterpart.
  examples: () => api.get('/api/examples'),
};

export const work = {
  queue: (params = {}) => api.get(`/api/me/queue${qs(params)}`),
  summary: () => api.get('/api/me/queue/summary'),
  task: (taskId, owner, preview) => {
    const params = new URLSearchParams();
    if (owner) params.set('owner', owner);
    if (preview) params.set('preview', preview);
    const qs = params.toString();
    return api.get(`/api/tasks/${taskId}${qs ? `?${qs}` : ''}`);
  },
  patchSample: (sampleId, payload, owner) =>
    api.patch(`/api/samples/${sampleId}/annotation${owner ? `?owner=${owner}` : ''}`, payload),
  patchTask: (taskId, samples, owner) =>
    api.patch(`/api/tasks/${taskId}/annotations${owner ? `?owner=${owner}` : ''}`, { samples }),
  submitTask: (taskId) => api.post(`/api/tasks/${taskId}/submit`),
  submitSample: (sampleId, owner) =>
    api.post(`/api/samples/${sampleId}/annotation/submit${owner ? `?owner=${owner}` : ''}`, {}),
  reopenSample: (sampleId, owner) =>
    api.post(`/api/samples/${sampleId}/annotation/reopen${owner ? `?owner=${owner}` : ''}`, {}),
  revisions: (sampleId, owner) =>
    api.get(`/api/samples/${sampleId}/annotation/revisions${owner ? `?owner=${owner}` : ''}`),
  snapshots: (sampleId, owner) =>
    api.get(`/api/samples/${sampleId}/annotation/snapshots${owner ? `?owner=${owner}` : ''}`),
  snapshot: (id) => api.get(`/api/annotations/snapshots/${id}`),
  pipeline: (sampleId) => api.get(`/api/samples/${sampleId}/pipeline`),
};

export const admin = {
  overview: (params = {}) => api.get(`/api/admin/stats/overview${qs(params)}`),
  annotators: (params = {}) => api.get(`/api/admin/stats/annotators${qs(params)}`),
  tags: (params = {}) => api.get(`/api/admin/stats/tags${qs(params)}`),
  datasets: () => api.get('/api/admin/stats/datasets'),
  activity: (days = 30) => api.get(`/api/admin/stats/activity?days=${days}`),

  users: (includeInactive = true) =>
    api.get(`/api/admin/users?include_inactive=${includeInactive}`),
  createUser: (payload) => api.post('/api/admin/users', payload),
  updateUser: (id, payload) => api.patch(`/api/admin/users/${id}`, payload),
  resetPassword: (id) => api.post(`/api/admin/users/${id}/reset-password`, {}),
  deactivateUser: (id) => api.del(`/api/admin/users/${id}`),

  assignments: (params = {}) => api.get(`/api/admin/assignments${qs(params)}`),
  batches: (params = {}) => api.get(`/api/admin/assignment-batches${qs(params)}`),
  availability: (params = {}) => api.get(`/api/admin/assignments/availability${qs(params)}`),
  previewAssignment: (payload) => api.post('/api/admin/assignments/preview', payload),
  createAssignment: (payload) => api.post('/api/admin/assignments', payload),
  revokeAssignment: (id, force) =>
    api.del(`/api/admin/assignments/${id}${force ? '?force=true' : ''}`),

  samples: (params = {}) => api.get(`/api/admin/samples${qs(params)}`),
  pipelineIndex: (params = {}) => api.get(`/api/admin/pipeline/index${qs(params)}`),
  ingestRuns: () => api.get('/api/admin/ingest/runs'),
  ingestConflicts: () => api.get('/api/admin/ingest/conflicts'),
  legacyConversions: () => api.get('/api/admin/legacy-conversions'),
  audit: (params = {}) => api.get(`/api/admin/audit${qs(params)}`),
};

function qs(params) {
  const entries = Object.entries(params).filter(
    ([, v]) => v !== undefined && v !== null && v !== '',
  );
  return entries.length ? `?${new URLSearchParams(entries)}` : '';
}

/**
 * Last-gasp save when the page is going away. `keepalive` (not sendBeacon) because a
 * beacon cannot carry the X-CSRF-Token header the server requires.
 */
export function saveOnUnload(path, payload) {
  try {
    fetch(path, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken || '' },
      credentials: 'include',
      body: JSON.stringify(payload),
      keepalive: true,
    });
  } catch {
    /* the page is unloading; nothing useful to do */
  }
}
